import axios from 'axios'

/**
 * 2要素認証（確認コード）の画面側。
 *
 * ここがやるのは、入力された文字列をサーバへ渡すことだけ。
 *
 * **誰のコードかは送らない。** 1つ目（パスワード / パスキー）を通した
 * ところまでの状態はサーバ側にあり、誰かはそこから決まる。画面から
 * 利用者IDを送る形にすると、その値を書き換えるだけで他人になれる。
 *
 * 認証アプリの数字と、予備の使い捨てコードは**同じ入口へ送る**。
 * どちらだったかをサーバは応答で区別しないので、画面も区別しない。
 */

const BASE = process.env.VUE_APP_API_ORIGIN

/**
 * 確認コードを送る。
 *
 * 通れば、パスワードのときと同じ形の応答が返る。
 */
export async function submitMfaCode (code) {
  const response = await axios.post(BASE + '/mfa/challenge', { code })
  return response.data
}

/**
 * 運営から聞き取った一時パスを送る（場面 S-48）。
 *
 * **確認コードと同じ入口ではない。** あちらは「持っている第2要素を
 * 出す」ための口で、こちらは「第2要素を失った人が、登録をやり直す」
 * ための口。混ぜると、断り方も回数の数え方も混ざる。
 *
 * 誰のパスかは送らない。確認コードのときと同じで、1つ目を通した
 * ところまでの状態はサーバ側にあり、誰かはそこから決まる。
 *
 * 通れば、2要素の登録しかできない状態へ移る。**ログインはしていない。**
 */
/**
 * 確認コード待ちの人が、一時パスの入口が開いているかを聞く（束22）。
 *
 * **Google / Microsoft から戻った人のために要る。**パスワード・パスキーで
 * 入った人はログインの応答に入口の開閉が載るが、外部IdPは画面へ飛ばして
 * 戻す形なので、こちらには ?mfa=1 の印しか届かない。聞き直さないと、
 * 入口を開けていても［認証アプリも予備のコードも使えない］が出ない。
 *
 * 誰かは送らない。サーバ側の「確認コード待ち」から決まる。返るのは
 * PREIKA 全体で入口が開いているかだけ。
 */
export async function fetchMfaChallengeState () {
  const response = await axios.get(BASE + '/mfa/challenge')
  return response.data
}

export async function useTemporaryRecoveryPass (pass) {
  const response = await axios.post(
    BASE + '/mfa/recovery-pass', { code: pass })
  return response.data
}

/**
 * 「2要素が近く必須になります」を、入ったあとにサーバへ聞く。
 *
 * **入り口によらず同じ案内を出すために要る。**
 *
 * 合言葉とパスキーで入った人は、ログインの応答に印が載っている。ところが
 * Google / Microsoft から入った人には載らない。あちらはサーバが画面へ
 * 飛ばして戻す形なので、応答の中身が途中で捨てられる。
 *
 * ログインの応答を控えておく形にすると、その応答を受け取れない人だけ
 * 案内が出ない。**同じ会社の同じ人が、入り口を変えただけで扱いが変わる。**
 *
 * 枠（TheContainer）が起動のときに1回ここを読めば、合言葉・パスキー・
 * Google・Microsoft・再読込・業務画面への直リンクが全部同じ道になる。
 */
export async function fetchAdminRequirementNotice () {
  const response = await axios.get(
    BASE + '/mfa/admin-requirement-notice')
  return response.data
}

/**
 * いまの段をサーバに聞き直す。
 *
 * **Google / Microsoft から入った人のために要る。**
 *
 * 合言葉で入った人は、ログインの応答に「あとで押せるか」が載っている。
 * ところが外部IdPは画面へ飛ばして戻す形なので、その応答は途中で捨て
 * られ、こちらには ?mfa=enroll という印しか届かない。
 *
 * 聞き直さないと、外部IdPから入った人だけ「あとで登録する」が出ない。
 * まだ促しの段なのに、登録するまで先へ進めなくなる。
 * **入り口によって振る舞いが変わるのがいちばん困る。**
 *
 * 逆に、こちらで勝手に「たぶん押せる」と決めてもいけない。押せるかを
 * 決めるのはサーバで、ここは表示のためだけに聞く。
 *
 * 誰かは送らない。サーバ側の「登録待ち」の状態から決まる。
 */
export async function fetchPendingEnrollmentState () {
  const response = await axios.get(
    BASE + '/mfa/enrollment/pending')
  return response.data
}

/**
 * 2要素の登録を始める（必須になった管理者）。
 *
 * **この人はまだログインしていない。** 1つ目は通っているが、会社が
 * 管理者の2要素を必須にした日を過ぎていて、まだ登録していない状態。
 *
 * 誰かは送らない。確認コードのときと同じで、サーバ側の状態から決まる。
 * 画面から利用者IDを送る形にすると、その値を書き換えるだけで他人になれる。
 *
 * 返る中身には共有鍵が入っている。**画面の外へ出さない。** 記録にも
 * 貼らない。1行残ると、そこからその人のコードを作れる。
 */
export async function startPendingEnrollment () {
  const response = await axios.post(
    BASE + '/mfa/enrollment/pending/start')
  return response.data
}

/**
 * 登録を仕上げる。**ここではまだログインしない。**
 *
 * 予備のコードが返る。**この1回しか見せられない。** 画面を閉じた
 * あとに取り直せる口は無い（あると、盗んだ人が取り直せる）。
 *
 * サーバもここではセッションを作らない。作ると、控える前に別のタブ
 * から業務画面へ届く。**画面で止めるのと、受けてから断るのは別。**
 */
export async function confirmPendingEnrollment (code) {
  const response = await axios.post(
    BASE + '/mfa/enrollment/pending/confirm', { code })
  return response.data
}

/**
 * 予備のコードを控えたので、ここで初めてログインする。
 *
 * 控えたかどうかを**サーバが確かめる方法は無い**。それでも、押す前に
 * 業務画面へ入れてしまう形は無くせる。
 *
 * 返るのは、パスワードのときと同じ形の応答。
 */
export async function completePendingEnrollment () {
  const response = await axios.post(
    BASE + '/mfa/enrollment/pending/complete')
  return response.data
}

/**
 * 登録を「あとで」にして、そのまま入る。
 *
 * **押せるかどうかはサーバが決める。** 画面が押せると思っていても、
 * そのあいだに施行日が来ていれば断られる。画面で期日を計算して
 * 決める形にすると、時計のずれた端末で押せてしまう。
 *
 * 押した回数はサーバが数える。上限に達した人は、次のログインから
 * 必須と同じ扱いになる。**押せば永久に先送りできる形にしない。**
 *
 * 返るのは、パスワードのときと同じ形の応答。
 */
export async function skipPendingEnrollment () {
  const response = await axios.post(
    BASE + '/mfa/enrollment/pending/skip')
  return response.data
}

/* ------------------------------------------------------------------ */
/* ログイン済みの本人が、自分で設定する                                 */
/* ------------------------------------------------------------------ */
/*
 * 上の pending 系は「必須になったので登録させられる」流れで、まだログイン
 * していない人が通る。ここから下は**もうログインしている人が自分から**
 * 2要素を付ける・外す・予備を配り直す流れ。
 *
 * サーバの口が別なのは、通る前提が違うため。pending 系はセッションに
 * 途中の印しか無く、こちらは利用者IDが入っている。**混ぜると、途中の
 * 状態で本人向けの操作ができてしまう。**
 */

/**
 * いまの状態。**画面を開いた最初に呼ぶ。**
 *
 * @returns {Promise<{enabled: boolean, backupCodesRemaining: number}>}
 */
export async function getMfaStatus () {
  const response = await axios.get(BASE + '/mfa/status')
  const data = response.data || {}
  return {
    enabled: Boolean(data.enabled),
    backupCodesRemaining: Number(data.backupCodesRemaining || 0),
    backupCodeBatch: Number(data.backupCodeBatch || 0),
    /*
     * やめられるか（束22）。**true / false / null の3つ。**
     * null は「サーバが答えていない」（古いサーバ・読めない）。画面は
     * そのときこれまでどおりボタンを出し、押されたらサーバが断る。
     * false と混ぜると、答えが無いだけでボタンが消える。
     */
    canDisable: typeof data.canDisable === 'boolean' ? data.canDisable : null
  }
}

/**
 * 自分で2要素の登録を始める。
 *
 * 返る中身には共有鍵が入っている。**画面の外へ出さない。**
 *
 * <b>ログインから15分を過ぎていると断られる。</b>取り返しがつかない操作
 * なので、サーバが本人確認のやり直しを求める。画面はそれを伝えるだけで、
 * ここで勝手に何かを試さない。
 */
export async function startEnrollment () {
  const response = await axios.post(BASE + '/mfa/enrollment/start')
  return response.data
}

/**
 * 6桁を通して登録を確定する。予備のコードが返る。
 *
 * **この1回しか見せられない。** 取り直せる口はあるが、それは
 * 「配り直し」で、前のぶんは全部使えなくなる。
 */
export async function confirmEnrollment (code) {
  const response = await axios.post(
    BASE + '/mfa/enrollment/confirm', { code })
  const data = response.data || {}
  return Array.isArray(data.backupCodes) ? data.backupCodes : []
}

/**
 * 予備のコードを配り直す。**前のぶんは全部使えなくなる。**
 *
 * expectedBatch には、いま画面が見ている組（getMfaStatus の
 * backupCodeBatch）を渡す。**別の画面が先に配り直していたら、サーバが
 * 何も消さずに断る。**
 *
 * これが無いと、2つのタブで押したときに順番に両方成功し、あとから通った
 * ほうが**先に画面へ出した10本を消す。**利用者はその10本を紙に控えて
 * いるので、スマホを失くした日に1本も通らない。
 *
 * @param {number} expectedBatch いま見ている組
 */
export async function reissueBackupCodes (expectedBatch) {
  /*
   * **取れていなければ叩かない。**
   *
   * Number(undefined || 0) は 0 になる。0 は「まだ1本も配っていない」
   * という**正しい版**なので、サーバは受け取って通してしまう。状態を
   * 読めていないだけなのに、配り直しが通ってしまう。
   */
  if (!Number.isInteger(expectedBatch) || expectedBatch < 0) {
    throw new Error('画面の状態を確認できませんでした。'
      + '画面を再読み込みしてから、もう一度お試しください。')
  }

  const response = await axios.post(BASE + '/mfa/backup-codes',
    { expectedBatch })
  const data = response.data || {}
  return Array.isArray(data.backupCodes) ? data.backupCodes : []
}

/**
 * 認証アプリを新しい端末へ付け替える。**1段目。**
 *
 * <b>いまの6桁か、予備のコードを出す。</b>それが通ると新しい鍵が返る。
 *
 * ここを本人確認のやり直しだけで通す作りにすると、合言葉とセッションを
 * 持っている相手が**第2要素を自分のものにできる。**2要素を足した意味が
 * そこで消える。だからサーバが必ず今の要素を確かめる。
 *
 * 返る鍵は画面の外へ出さない。記録にも貼らない。
 *
 * **この時点ではまだ何も入れ替わっていない。**古い端末はそのまま使える。
 *
 * 返る rotationId は**この1回ぶんの名前**。仕上げのときに返す。
 * 同じブラウザの2つのタブはサーバ側のセッションを共有するので、名前が
 * 無いと**2枚目が1枚目の鍵を黙って上書きする。**1枚目の人は、正しく
 * 読み取って正しく入力したのに断られる。
 */
export async function startRotation (code) {
  const response = await axios.post(BASE + '/mfa/rotation/start', { code })
  return response.data
}

/**
 * 新しい端末の6桁で入れ替える。**2段目。ここで初めて入れ替わる。**
 *
 * 予備のコードが返る。鍵が変わったので、**前の紙は使えなくなる。**
 */
export async function confirmRotation (rotationId, code) {
  /*
   * **名前が無ければ叩かない。**サーバは知らない名前を断るが、
   * ここで送らないと「始めていない」と同じ扱いになり、利用者には
   * 何が起きたのか分からない。
   */
  if (!rotationId) {
    throw new Error('付け替えの途中の状態が失われました。'
      + 'もう一度はじめからお試しください。')
  }

  const response = await axios.post(
    BASE + '/mfa/rotation/confirm', { rotationId, code })
  const data = response.data || {}
  return Array.isArray(data.backupCodes) ? data.backupCodes : []
}

/**
 * 付け替えをやめる。**サーバの預かりぶんも捨てる。**
 *
 * 画面を閉じるだけにすると、「やめた」のにサーバではまだ仕上げられる
 * 状態が残る。押した意味とずれる。
 */
export async function cancelRotation (rotationId) {
  if (!rotationId) { return }
  await axios.post(BASE + '/mfa/rotation/cancel', { rotationId })
}

/**
 * 2要素をやめる。予備のコードも一緒に消える。
 *
 * <b>会社が管理者に必須としている場合は、本人でも断られる。</b>
 * そのときサーバは 403 と理由を返すので、画面はその文言を出す。
 */
export async function disableMfa () {
  await axios.delete(BASE + '/mfa')
}

/**
 * 会社の方針で断られたか。
 *
 * 本人確認のやり直し（同じ403）と区別する。**混ぜると、「ログインし直せば
 * 外せる」と誤解させる。**方針で断られているときは、何度ログインし直しても
 * 外せない。
 */
export function isForbiddenByPolicy (error) {
  const response = error && error.response
  if (!response || response.status !== 403) return false
  const data = response.data || {}
  return String(data.code || '') !== 'REAUTHENTICATION_REQUIRED'
}

/*
 * 本人確認のやり直しの見分け方は1か所しか持たない。
 * 同じ判定を2つ書くと、サーバが印を変えたときに片方だけ直る。
 */
export { isReauthenticationRequired } from './passkey'

/** サーバが返した文言。無ければ null。 */
export function serverMessage (error) {
  const response = error && error.response
  const data = (response && response.data) || {}
  const message = data.message
  return typeof message === 'string' && message ? message : null
}

/**
 * 途中の状態が消えているか。
 *
 * <p>コードそのものについての理由は、いまも区別しない。「合わない」と
 * 「さっき使った」を見分けられると、**当たっていたことまで分かる。**</p>
 *
 * <p>区別するのは、待ち状態が消えたことだけ。ここを見ないと、画面は
 * **二度と通らない入力欄**を出したままになり、利用者は正しいコードを
 * 入れ続ける（場面 S-61）。ブラウザは自分の時計で同じことを知れるので、
 * これを出しても何も漏れない。</p>
 */
export function isChallengeGone (error) {
  const response = error && error.response
  const data = (response && response.data) || {}
  return data.code === 'MFA_CHALLENGE_GONE'
}

/**
 * 通信そのものが届かなかったか。
 */
export { isNetworkFailure } from './passkey'
